"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.statementMatchesResponse = exports.statementMatchCandidate = exports.matchReason = exports.importStatementResponse = exports.statementLineDto = exports.importStatementRequest = exports.statementImportLine = exports.reconciliationDto = exports.createReconciliationRequest = exports.reconciliationWorkspace = exports.reconcilableLine = exports.reconciliationWorkspaceQuery = exports.treasuryAccountsResponse = exports.bankAccountDto = exports.cashboxDto = exports.setActiveRequest = exports.updateBankAccountRequest = exports.updateCashboxRequest = exports.createBankAccountRequest = exports.createCashboxRequest = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
// ---------------------------------------------------------------------------
// الخزائن والحسابات البنكية
// ---------------------------------------------------------------------------
const nameAr = zod_1.z.string().trim().min(1).max(200);
/**
 * خزينة على حساب أستاذ دفتره المساعد `cashbox`، لا يربطه بخزينة نشطة أخرى. بلا
 * `accountCode` يُفتح لها حساب فرعى جديد تحت النقدية إن كان إعداد الشركة
 * `treasury.autoCreateLedgerAccount` مفعّلاً (ADR-0006)، وإلا رُفض الطلب.
 */
exports.createCashboxRequest = zod_1.z
    .object({ nameAr, accountCode: common_1.accountCode.optional() })
    .strict();
/**
 * IBAN بالصيغة الدولية بلا مسافات: كود الدولة ورقما تحقق ثم ١١–٣٠ حرفًا ورقمًا.
 * لا يُتحقق من رقم التحقق — البنك هو المرجع، والحقل للعرض والتعريف.
 */
const iban = zod_1.z
    .string()
    .trim()
    .transform((value) => value.replace(/\s+/g, '').toUpperCase())
    .pipe(zod_1.z.string().regex(/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/, 'invalid IBAN'));
exports.createBankAccountRequest = zod_1.z
    .object({
    nameAr,
    /** بلا كود: حساب فرعى جديد تحت البنوك والمحافظ — كالخزينة. */
    accountCode: common_1.accountCode.optional(),
    bankName: zod_1.z.string().trim().min(1).max(200).optional(),
    iban: iban.optional(),
})
    .strict();
/** تعديل اسم الخزينة — حساب الأستاذ المربوط لا يتغير، فرصيدها وحركاتها كما هى. */
exports.updateCashboxRequest = zod_1.z.object({ nameAr }).strict();
/** تعديل بيانات الحساب البنكى للعرض — حساب الأستاذ ثابت كالخزينة. */
exports.updateBankAccountRequest = zod_1.z
    .object({
    nameAr,
    bankName: zod_1.z.string().trim().max(200).nullish(),
    iban: iban.nullish(),
})
    .strict();
exports.setActiveRequest = zod_1.z.object({ isActive: zod_1.z.boolean() }).strict();
exports.cashboxDto = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    nameAr: zod_1.z.string(),
    accountCode: common_1.accountCode,
    accountNameAr: zod_1.z.string(),
    currencyCode: zod_1.z.string(),
    isActive: zod_1.z.boolean(),
    /** رصيد حساب الأستاذ المنشور حتى `asOf`. */
    balance: common_1.moneyString,
    createdAt: zod_1.z.string(),
});
exports.bankAccountDto = exports.cashboxDto.extend({
    bankName: zod_1.z.string().nullable(),
    iban: zod_1.z.string().nullable(),
    lastReconciledDate: common_1.isoDate.nullable(),
    lastReconciledBalance: common_1.moneyString.nullable(),
});
exports.treasuryAccountsResponse = zod_1.z.object({
    companyId: common_1.uuid,
    asOf: common_1.isoDate,
    cashboxes: zod_1.z.array(exports.cashboxDto),
    bankAccounts: zod_1.z.array(exports.bankAccountDto),
});
// ---------------------------------------------------------------------------
// المطابقة البنكية
// ---------------------------------------------------------------------------
exports.reconciliationWorkspaceQuery = zod_1.z.object({ statementDate: common_1.isoDate }).strict();
exports.reconcilableLine = zod_1.z.object({
    journalLineId: common_1.uuid,
    journalId: common_1.uuid,
    journalNumber: zod_1.z.string().nullable(),
    postingDate: common_1.isoDate,
    reference: zod_1.z.string().nullable(),
    memo: zod_1.z.string().nullable(),
    debit: common_1.moneyString,
    credit: common_1.moneyString,
});
/** كل ما تحتاجه شاشة المطابقة لكشف بتاريخ: الرصيد السابق والسطور المعلقة حتى التاريخ. */
exports.reconciliationWorkspace = zod_1.z.object({
    bankAccountId: common_1.uuid,
    statementDate: common_1.isoDate,
    previousDate: common_1.isoDate.nullable(),
    previousBalance: common_1.moneyString,
    /** رصيد الدفتر على الحساب فى تاريخ الكشف. */
    bookBalance: common_1.moneyString,
    lines: zod_1.z.array(exports.reconcilableLine),
});
exports.createReconciliationRequest = zod_1.z
    .object({
    statementDate: common_1.isoDate,
    /** رصيد كشف البنك الختامى — قد يكون سالبًا لحساب مكشوف. */
    statementBalance: common_1.moneyString,
    journalLineIds: zod_1.z.array(common_1.uuid).max(5000),
    /**
     * سطور الكشف المستورد التى بُنيت عليها المطابقة — تُربط بها فتصير ثابتة. اختيارى:
     * المطابقة نفسها تُقبل بنفس القواعد بكشف مستورد أو بدونه.
     */
    statementLineIds: zod_1.z.array(common_1.uuid).max(5000).optional(),
    memo: zod_1.z.string().trim().max(500).optional(),
})
    .strict();
exports.reconciliationDto = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    bankAccountId: common_1.uuid,
    statementDate: common_1.isoDate,
    statementBalance: common_1.moneyString,
    previousBalance: common_1.moneyString,
    bookBalance: common_1.moneyString,
    clearedCount: zod_1.z.number().int(),
    clearedDeposits: common_1.moneyString,
    clearedWithdrawals: common_1.moneyString,
    memo: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
});
// ---------------------------------------------------------------------------
// استيراد كشف البنك واقتراح المطابقة
// ---------------------------------------------------------------------------
/** مبلغ بإشارته غير صفرى: الإيداع موجب والسحب سالب. */
const signedAmount = common_1.moneyString.refine((value) => /[1-9]/.test(value), 'amount must not be zero');
/**
 * سطر كشف بعد تطبيع الواجهة: الملف يُقرأ فى المتصفح ويُختار عمود كل حقل، ثم يصل الخادم
 * صفوفًا موحّدة يتحقق منها هنا. الملف الخام لا يُرفع ولا يُخزَّن.
 */
exports.statementImportLine = zod_1.z
    .object({
    date: common_1.isoDate,
    description: zod_1.z.string().trim().max(500).default(''),
    reference: zod_1.z.string().trim().max(200).optional(),
    amount: signedAmount,
    /** الرصيد بعد الحركة كما فى ملف البنك، إن وُجد عموده. */
    balance: common_1.moneyString.optional(),
})
    .strict();
exports.importStatementRequest = zod_1.z
    .object({
    fileName: zod_1.z.string().trim().max(200).optional(),
    lines: zod_1.z.array(exports.statementImportLine).min(1).max(5000),
})
    .strict();
exports.statementLineDto = zod_1.z.object({
    id: common_1.uuid,
    importId: common_1.uuid,
    lineNumber: zod_1.z.number().int(),
    date: common_1.isoDate,
    description: zod_1.z.string(),
    reference: zod_1.z.string().nullable(),
    amount: common_1.moneyString,
    balance: common_1.moneyString.nullable(),
    reconciliationId: common_1.uuid.nullable(),
});
exports.importStatementResponse = zod_1.z.object({
    importId: common_1.uuid,
    /** سطور جديدة أُضيفت. */
    inserted: zod_1.z.number().int(),
    /** سطور تجاهلها منع التكرار لأنها استُوردت من قبل. */
    duplicates: zod_1.z.number().int(),
    lines: zod_1.z.array(exports.statementLineDto),
});
exports.matchReason = zod_1.z.enum(['AMOUNT', 'SAME_DATE', 'NEAR_DATE', 'DOCUMENT_NUMBER']);
exports.statementMatchCandidate = zod_1.z.object({
    journalLineId: common_1.uuid,
    score: zod_1.z.number().int(),
    dayGap: zod_1.z.number().int(),
    reasons: zod_1.z.array(exports.matchReason),
});
/**
 * سطور الكشف غير المُطابقة حتى التاريخ ولكل منها المرشحون من سطور الدفتر المعلقة
 * والمقترح بعد التوزيع واحدًا لواحد. `closingBalance` رصيد آخر سطر معلق حتى التاريخ.
 */
exports.statementMatchesResponse = zod_1.z.object({
    bankAccountId: common_1.uuid,
    statementDate: common_1.isoDate,
    closingBalance: common_1.moneyString.nullable(),
    lines: zod_1.z.array(exports.statementLineDto.extend({
        candidates: zod_1.z.array(exports.statementMatchCandidate),
        suggestedJournalLineId: common_1.uuid.nullable(),
    })),
});
//# sourceMappingURL=treasury.js.map